// Studio sidebar — the familiar right-rail inspector pattern (Figma/Photoshop).
//
// Why this layout: the previous dock was unique but cramped — small icons, no
// labels, vertical-drag knobs. A right sidebar with clear section headers and
// standard labeled horizontal sliders is the most intuitive shape for an
// inspector — every design tool uses it, so users know it on first sight.

import { effect } from '@/core/signals';
import { state, updateLayer, beginEdit, endEdit, setPaused } from '@/core/state';
import { findDotgradLayer, exitStudio } from '@/lib/studioMode';
import { SWATCHES, type Stop } from '@/lib/swatches';
import { MOTION_PRESETS } from '@/lib/motionPresets';
import { SHAPE_GROUPS, SHAPE_IDS, shapeLabel } from '@/generators/shapes';
import { RETRO_PRESETS, getRetroPreset } from '@/lib/retroPresets';
import { stopsBar } from './stopsBar';
import { el, on, replaceChildren } from '@/lib/dom';
import { mulberry32 } from '@/lib/rng';
import { serializeDsl } from '@/core/dsl';
import { slider } from '@/ui/controls/slider';
import { toggleControl } from '@/ui/controls/toggle';
import { colorControl } from '@/ui/controls/colorSwatch';
import { selectControl } from '@/ui/controls/select';
import type { Layer, BlendMode } from '@/core/types';

type Shape = string;

export function mountStudioPanel(host: HTMLElement): () => void {
  host.classList.add('st-sidebar-host');

  // ---- Sidebar (right rail) -----------------------------------------------
  const sidebar = el('aside', { class: 'st-side', role: 'complementary', ariaLabel: 'Studio inspector' });

  // Header with brand + shuffle + close
  const head = el('div', { class: 'st-side-head' }, [
    el('div', { class: 'st-side-brand' }, [
      el('span', { class: 'st-side-dot' }),
      el('strong', { class: 'st-side-title' }, ['Studio']),
    ]),
    actionsRow(),
  ]);

  const scroll = el('div', { class: 'st-side-scroll' });
  sidebar.append(head, scroll);

  // Sections
  const retroSec = section('Retro');
  const retroSelect = el('select', { class: 'sel-input st-shape-select', ariaLabel: 'Retro preset' }) as HTMLSelectElement;
  const blank = el('option', { value: '' }) as HTMLOptionElement;
  blank.textContent = '— pick a retro preset —';
  retroSelect.appendChild(blank);
  for (const group of RETRO_PRESETS) {
    const og = document.createElement('optgroup');
    og.label = group.label;
    for (const pr of group.presets) {
      const opt = el('option', { value: pr.id }) as HTMLOptionElement;
      opt.textContent = pr.name;
      og.appendChild(opt);
    }
    retroSelect.appendChild(og);
  }
  on(retroSelect, 'change', () => {
    if (!retroSelect.value) return;
    applyRetroPresetById(retroSelect.value);
  });
  retroSec.body.appendChild(retroSelect);

  const shapeSec = section('Shape');
  const shapeSelect = el('select', { class: 'sel-input st-shape-select', ariaLabel: 'Shape' }) as HTMLSelectElement;
  for (const group of SHAPE_GROUPS) {
    const og = document.createElement('optgroup');
    og.label = group.label;
    for (const id of group.ids) {
      const opt = el('option', { value: id }) as HTMLOptionElement;
      opt.textContent = shapeLabel(id);
      og.appendChild(opt);
    }
    shapeSelect.appendChild(og);
  }
  on(shapeSelect, 'change', () => {
    const layer = findDotgradLayer();
    if (layer) updateLayer(layer.id, { params: { shape: shapeSelect.value } });
  });
  shapeSec.body.appendChild(shapeSelect);

  const colorsSec = section('Colors');
  const swatchGrid = el('div', { class: 'st-swatch-grid' });
  const stopsHost = el('div', { class: 'st-stops-host' });
  colorsSec.body.append(
    swatchGrid,
    el('div', { class: 'st-mini-label' }, ['Stops — drag, click to recolor, double-click to remove, click bar to add']),
    stopsHost,
  );

  const adjustSec = section('Adjust');
  const adjustHost = el('div', { class: 'st-adjust-host' });
  adjustSec.body.appendChild(adjustHost);

  const styleSec = section('Style');
  const styleHost = el('div', { class: 'st-style-host' });
  styleSec.body.appendChild(styleHost);

  const motionSec = section('Motion');
  const motionHost = el('div', { class: 'st-motion-host' });
  motionSec.body.appendChild(motionHost);

  scroll.append(retroSec.root, shapeSec.root, colorsSec.root, adjustSec.root, styleSec.root, motionSec.root);

  // ---- Sidebar footer (DSL + Export) --------------------------------------
  const foot = el('div', { class: 'st-side-foot' });
  const dsl = el('button', { class: 'st-dsl mono', ariaLabel: 'Copy DSL', title: 'Click to copy' });
  on(dsl, 'click', async () => {
    try {
      await navigator.clipboard.writeText(serializeDsl(state.peek()));
      dsl.classList.add('flash');
      setTimeout(() => dsl.classList.remove('flash'), 700);
    } catch { /* ignored */ }
  });
  const exportBtn = el('button', { class: 'st-foot-btn', ariaLabel: 'Export' }, ['Export ↗']);
  on(exportBtn, 'click', async () => {
    const m = await import('@/ui/exportDialog');
    m.openExportDialog();
  });
  foot.append(dsl, exportBtn);
  sidebar.append(foot);

  host.append(sidebar);

  // ---- Swatches (4×6 grid, all visible) -----------------------------------
  const swatchButtons: HTMLButtonElement[] = [];
  for (const sw of SWATCHES) {
    const btn = el('button', { class: 'st-side-swatch', title: sw.name, ariaLabel: sw.name }) as HTMLButtonElement;
    btn.style.background = `linear-gradient(to right, ${sw.stops.map((s) => `${s.color} ${s.t * 100}%`).join(', ')})`;
    on(btn, 'click', () => {
      const layer = findDotgradLayer();
      if (!layer) return;
      beginEdit();
      updateLayer(layer.id, { params: { stops: [...sw.stops] } });
      endEdit();
    });
    swatchButtons.push(btn);
    swatchGrid.appendChild(btn);
  }

  // ---- Reactive: rebuild Adjust + Style + Motion when layer changes -------
  let lastShape: Shape | null = null;
  let lastStopsSig = '';
  let lastMotion: string | null = null;
  let lastPaused: boolean | null = null;
  let playBtn: HTMLButtonElement | null = null;
  const dispose = effect(() => {
    const s = state.get();
    const layer = findDotgradLayer();
    if (!layer) return;

    const shape = (layer.params['shape'] as Shape) ?? 'linear';
    if (shapeSelect.value !== shape) shapeSelect.value = shape;

    const sig = stopsSig(layer.params['stops'] as Stop[] | undefined);
    for (let i = 0; i < SWATCHES.length; i++) {
      const swSig = stopsSig(SWATCHES[i]!.stops);
      swatchButtons[i]!.classList.toggle('active', swSig === sig);
    }

    if (sig !== lastStopsSig) {
      lastStopsSig = sig;
      const stops = (layer.params['stops'] as Stop[]) ?? [
        { t: 0, color: '#000000' },
        { t: 1, color: '#ffffff' },
      ];
      replaceChildren(
        stopsHost,
        stopsBar({
          stops,
          onBegin: beginEdit,
          onEnd: endEdit,
          onChange: (next) => {
            const fresh = findDotgradLayer();
            if (fresh) updateLayer(fresh.id, { params: { stops: next } });
          },
        }),
      );
    }

    if (shape !== lastShape) {
      lastShape = shape;
      replaceChildren(adjustHost, ...buildAdjustRows(layer, shape));
      replaceChildren(styleHost, ...buildStyleRows(layer));
    } else {
      // Refresh values without re-mounting
      refreshAdjust(adjustHost, layer);
      refreshStyle(styleHost, layer);
    }

    const motion = (layer.params['motion'] as string) ?? 'none';
    if (motion !== lastMotion) {
      lastMotion = motion;
      const built = buildMotionRows(layer, motion);
      replaceChildren(motionHost, ...built.rows);
      playBtn = built.playBtn;
    }
    if (s.paused !== lastPaused) {
      lastPaused = s.paused;
      if (playBtn) playBtn.textContent = s.paused ? '▶ Play' : '❚❚ Pause';
    }

    dsl.textContent = serializeDsl(s);
  });

  return () => dispose();
}

// ---- Section helper -------------------------------------------------------

function section(title: string): { root: HTMLElement; body: HTMLElement } {
  const body = el('div', { class: 'st-sec-body' });
  const root = el('div', { class: 'st-sec' }, [
    el('div', { class: 'st-sec-head' }, [title]),
    body,
  ]);
  return { root, body };
}

// ---- Stops signature ------------------------------------------------------

function stopsSig(stops: Stop[] | undefined): string {
  if (!stops) return '';
  return stops.map((s) => `${s.t.toFixed(3)}|${s.color.toLowerCase()}`).join(',');
}

// ---- Adjust rows (full standard sliders with labels + values) -----------

type AdjustField = {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  format?: (v: number) => string;
  showFor?: (shape: Shape) => boolean;
};

function adjustFields(): AdjustField[] {
  return [
    { key: 'angle',   label: 'Angle',    min: 0,    max: 360,  step: 1, format: (v) => `${Math.round(v)}°` },
    { key: 'spread',  label: 'Spread',   min: 8,    max: 2048, step: 1 },
    { key: 'offset',  label: 'Offset',   min: -100, max: 100,  step: 1 },
    { key: 'repeat',  label: 'Repeat',   min: 1,    max: 8,    step: 1 },
    { key: 'cell',    label: 'Pixel',    min: 2,    max: 64,   step: 1, format: (v) => `${Math.round(v)} px` },
    { key: 'quant',   label: 'Quantize', min: 0,    max: 16,   step: 1, format: (v) => v === 0 ? 'off' : String(Math.round(v)) },
    { key: 'centerX', label: 'Center X', min: -100, max: 100,  step: 1 },
    { key: 'centerY', label: 'Center Y', min: -100, max: 100,  step: 1 },
  ];
}

function buildAdjustRows(layer: Layer, shape: Shape): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const f of adjustFields()) {
    if (f.showFor && !f.showFor(shape)) continue;
    const value = typeof layer.params[f.key] === 'number' ? (layer.params[f.key] as number) : f.min;
    const opts: Parameters<typeof slider>[0] = {
      label: f.label,
      min: f.min,
      max: f.max,
      step: f.step,
      value,
      onInput: (v) => {
        const fresh = findDotgradLayer();
        if (fresh) updateLayer(fresh.id, { params: { [f.key]: Math.round(v) } });
      },
      onBegin: beginEdit,
      onEnd: endEdit,
    };
    if (f.format) opts.format = f.format;
    const ctrl = slider(opts);
    ctrl.dataset['key'] = f.key;
    out.push(ctrl);
  }
  // Mirror + OKLab toggles
  out.push(
    el('div', { class: 'st-toggle-row' }, [
      toggleControl({
        label: 'Mirror',
        value: !!layer.params['mirror'],
        onChange: (v) => {
          const fresh = findDotgradLayer();
          if (fresh) updateLayer(fresh.id, { params: { mirror: v } });
        },
      }),
      toggleControl({
        label: 'OKLab interp',
        value: layer.params['oklab'] !== false,
        onChange: (v) => {
          const fresh = findDotgradLayer();
          if (fresh) updateLayer(fresh.id, { params: { oklab: v } });
        },
      }),
    ]),
  );
  return out;
}

function refreshAdjust(host: HTMLElement, layer: Layer): void {
  // Slider exposes a `refresh` method we set up internally; calling .value
  // setter via the slider widget API isn't exposed, so simply rebuild on
  // shape-change only. For non-shape edits, slider's own state stays in sync
  // because it owns the visual (the user just dragged it).
  void host;
  void layer;
}

function buildStyleRows(layer: Layer): HTMLElement[] {
  return [
    colorControl({
      label: 'Background',
      value: typeof layer.params['bg'] === 'string' ? (layer.params['bg'] as string) : '#000000',
      onChange: (v) => {
        const fresh = findDotgradLayer();
        if (fresh) updateLayer(fresh.id, { params: { bg: v } });
      },
      onBegin: beginEdit,
      onEnd: endEdit,
    }),
    slider({
      label: 'Gap',
      min: 0,
      max: 16,
      step: 1,
      value: typeof layer.params['gap'] === 'number' ? (layer.params['gap'] as number) : 1,
      format: (v) => `${Math.round(v)} px`,
      onInput: (v) => {
        const fresh = findDotgradLayer();
        if (fresh) updateLayer(fresh.id, { params: { gap: Math.round(v) } });
      },
      onBegin: beginEdit,
      onEnd: endEdit,
    }),
    slider({
      label: 'Noise',
      min: 0,
      max: 100,
      step: 1,
      value: typeof layer.params['noise'] === 'number' ? (layer.params['noise'] as number) : 0,
      format: (v) => v === 0 ? 'off' : `${Math.round(v)}%`,
      onInput: (v) => {
        const fresh = findDotgradLayer();
        if (fresh) updateLayer(fresh.id, { params: { noise: Math.round(v) } });
      },
      onBegin: beginEdit,
      onEnd: endEdit,
    }),
  ];
}

function refreshStyle(host: HTMLElement, layer: Layer): void {
  void host;
  void layer;
}

// ---- Motion rows ---------------------------------------------------------

function buildMotionRows(layer: Layer, currentMotion: string): { rows: HTMLElement[]; playBtn: HTMLButtonElement } {
  const motionIds = ['none', ...MOTION_PRESETS.map((m) => m.id)];
  const motionLabels: Record<string, string> = { none: 'None' };
  for (const m of MOTION_PRESETS) motionLabels[m.id] = m.name;

  const presetRow = selectControl({
    label: 'Preset',
    value: currentMotion,
    options: motionIds,
    labels: motionLabels,
    onChange: (v) => {
      const fresh = findDotgradLayer();
      if (!fresh) return;
      updateLayer(fresh.id, { params: { motion: v } });
      setPaused(v === 'none');
    },
  });

  const speedSlider = slider({
    label: 'Speed',
    min: 10,
    max: 400,
    step: 1,
    value: typeof layer.params['motionSpeed'] === 'number' ? (layer.params['motionSpeed'] as number) : 100,
    format: (v) => `${Math.round(v)}%`,
    onInput: (v) => {
      const fresh = findDotgradLayer();
      if (fresh) updateLayer(fresh.id, { params: { motionSpeed: Math.round(v) } });
    },
    onBegin: beginEdit,
    onEnd: endEdit,
  });

  const strengthSlider = slider({
    label: 'Strength',
    min: 0,
    max: 200,
    step: 1,
    value: typeof layer.params['motionStrength'] === 'number' ? (layer.params['motionStrength'] as number) : 100,
    format: (v) => `${Math.round(v)}%`,
    onInput: (v) => {
      const fresh = findDotgradLayer();
      if (fresh) updateLayer(fresh.id, { params: { motionStrength: Math.round(v) } });
    },
    onBegin: beginEdit,
    onEnd: endEdit,
  });

  const playBtn = el('button', {
    class: 'st-motion-play',
    ariaLabel: 'Play/Pause animation',
    title: 'Play/Pause',
  }, [state.peek().paused ? '▶ Play' : '❚❚ Pause']) as HTMLButtonElement;
  on(playBtn, 'click', () => setPaused(!state.peek().paused));

  return { rows: [presetRow, speedSlider, strengthSlider, playBtn], playBtn };
}

// ---- Header actions row ---------------------------------------------------

function actionsRow(): HTMLElement {
  const row = el('div', { class: 'st-side-actions' });
  const bright = el('button', { class: 'st-side-iconbtn', ariaLabel: 'Bright preset', title: 'Bright preset (clean dot grid on white)' }, ['✨']);
  on(bright, 'click', () => brightPreset());
  const shuffle = el('button', { class: 'st-side-iconbtn', ariaLabel: 'Shuffle (R)', title: 'Shuffle' }, ['↯']);
  on(shuffle, 'click', () => randomizeDotgrad());
  const close = el('button', { class: 'st-side-iconbtn st-side-close', ariaLabel: 'Exit Studio (M / Esc)', title: 'Exit' }, ['×']);
  on(close, 'click', () => exitStudio());
  row.append(bright, shuffle, close);
  return row;
}

// ---- Bright preset --------------------------------------------------------
// One-click reset to a clean, vibrant configuration: dot-grid shape, no noise,
// no quantization, white background, two-stop gradient. Cycles through a few
// color combos so repeat clicks give variety without ever producing dim output.

const BRIGHT_VARIANTS: { shape: string; stops: Stop[]; bg: string; cell: number; spread: number }[] = [
  { shape: 'dots-grid',  stops: [{ t: 0, color: '#00cc66' }, { t: 1, color: '#ffffff' }], bg: '#ffffff', cell: 4,  spread: 480 },
  { shape: 'dots-grid',  stops: [{ t: 0, color: '#ff3d8b' }, { t: 1, color: '#ffffff' }], bg: '#ffffff', cell: 4,  spread: 480 },
  { shape: 'dots-grid',  stops: [{ t: 0, color: '#1a7fff' }, { t: 1, color: '#ffffff' }], bg: '#ffffff', cell: 4,  spread: 480 },
  { shape: 'radial',     stops: [{ t: 0, color: '#ff5a1f' }, { t: 0.5, color: '#ffd166' }, { t: 1, color: '#ffffff' }], bg: '#ffffff', cell: 6, spread: 800 },
  { shape: 'rose-5',     stops: [{ t: 0, color: '#7733ff' }, { t: 1, color: '#ffaaee' }], bg: '#ffffff', cell: 4,  spread: 600 },
  { shape: 'sine-radial',stops: [{ t: 0, color: '#00bbff' }, { t: 1, color: '#ffffff' }], bg: '#ffffff', cell: 4,  spread: 400 },
];
let brightVariantIndex = 0;

function applyRetroPresetById(id: string): void {
  const layer = findDotgradLayer();
  if (!layer) return;
  const preset = getRetroPreset(id);
  if (!preset) return;
  beginEdit();
  updateLayer(layer.id, {
    params: {
      shape: preset.shape,
      angle: preset.angle ?? 0,
      spread: preset.spread,
      offset: preset.offset ?? 0,
      repeat: preset.repeat ?? 1,
      centerX: preset.centerX ?? 0,
      centerY: preset.centerY ?? 0,
      cell: preset.cell,
      gap: preset.gap,
      quant: preset.quant ?? 0,
      noise: preset.noise ?? 0,
      mirror: preset.mirror ?? false,
      oklab: preset.oklab ?? true,
      bg: preset.bg,
      stops: [...preset.stops],
      motion: 'none',
      motionSpeed: 100,
      motionStrength: 100,
    },
  });
  endEdit();
}

function brightPreset(): void {
  const layer = findDotgradLayer();
  if (!layer) return;
  const variant = BRIGHT_VARIANTS[brightVariantIndex % BRIGHT_VARIANTS.length]!;
  brightVariantIndex++;
  beginEdit();
  updateLayer(layer.id, {
    params: {
      shape: variant.shape,
      angle: 0,
      spread: variant.spread,
      offset: 0,
      repeat: 1,
      centerX: 0,
      centerY: 0,
      cell: variant.cell,
      gap: 0,        // no black gap bleed
      quant: 0,      // no banding
      noise: 0,      // no speckle
      mirror: false,
      oklab: true,
      bg: variant.bg,
      stops: [...variant.stops],
      motion: 'none',
      motionSpeed: 100,
      motionStrength: 100,
    },
  });
  endEdit();
}

// ---- Randomize ------------------------------------------------------------

function randomizeDotgrad(): void {
  const layer = findDotgradLayer();
  if (!layer) return;
  const rng = mulberry32(Date.now() & 0xffffffff);
  const swatch = SWATCHES[Math.floor(rng() * SWATCHES.length)] ?? SWATCHES[0]!;
  beginEdit();
  updateLayer(layer.id, {
    params: {
      shape: SHAPE_IDS[Math.floor(rng() * SHAPE_IDS.length)] ?? 'linear',
      angle: Math.floor(rng() * 360),
      spread: 100 + Math.floor(rng() * 800),
      offset: Math.floor(rng() * 200) - 100,
      repeat: 1 + Math.floor(rng() * 4),
      centerX: Math.floor(rng() * 80) - 40,
      centerY: Math.floor(rng() * 80) - 40,
      cell: 4 + Math.floor(rng() * 8),
      gap: Math.floor(rng() * 3),
      quant: rng() < 0.5 ? 0 : Math.floor(rng() * 8 + 2),
      noise: rng() < 0.7 ? 0 : Math.floor(rng() * 30),
      mirror: rng() < 0.4,
      oklab: rng() < 0.85,
      stops: [...swatch.stops],
    },
    seed: Math.floor(rng() * 0x7fffffff),
  });
  endEdit();
}

// suppress unused-import linting
void ({} as BlendMode);
